import type { FieldSuggestion } from '../types/framework';

export type TransformLog = { plugin: string; message: string };

type Logged = Pick<FieldSuggestion, 'value' | 'steps'> & { source?: string };

const MAX_VALUE_LENGTH = 60;

const lineKey = ({ plugin, message }: TransformLog) => JSON.stringify([plugin, message]);

const shown = (value: unknown) => {
  const text = typeof value === 'string' ? value.trim() : JSON.stringify(value);
  if (text === '' || text === undefined) return '(empty)';
  return text.length > MAX_VALUE_LENGTH ? `${text.slice(0, MAX_VALUE_LENGTH)}…` : text;
};

/**
 * One log line per suggestion a transformer plugin made, so the plugin logs
 * show what these plugins did: "Pickup reference → PU-4471" under Split.
 * A suggestion is a transformer's when it came out of the transform chain
 * (its steps) or from a transformer plugin itself, like a date format.
 *
 * `pluginNames` maps step names and plugin keys to the plugin's name. Lines
 * already in `logged` are left out: a field is evaluated again on every load
 * and re-evaluation.
 */
export const transformLogs = (
  fieldName: string,
  suggestions: readonly (Logged | void)[],
  pluginNames: Map<string, string>,
  logged: readonly TransformLog[] = [],
): TransformLog[] => {
  const seen = new Set(logged.map(lineKey));
  return suggestions.flatMap((suggestion) => {
    if (!suggestion) return [];
    const makers = suggestion.steps?.length ? suggestion.steps : [suggestion.source ?? ''];
    const plugins = [...new Set(makers.map((name) => pluginNames.get(name)))];
    if (plugins.some((plugin) => plugin === undefined)) return [];
    const line = { plugin: plugins.join(' + '), message: `${fieldName} → ${shown(suggestion.value)}` };
    if (seen.has(lineKey(line))) return [];
    seen.add(lineKey(line));
    return [line];
  });
};
